import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Package, User, ClipboardList, MapPin, Search, Check, ChevronsUpDown, X } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { clientsApi, type ClientForSelector } from '@/api/clients';
import type { TaskType, CreateTaskDto } from '@/api/tasks';
import type { OwnershipFilter } from '@shared/types';

const OWNERSHIP_FILTER_KEY = 'visits-ownership-filter';

function getOwnershipFilter(): OwnershipFilter | undefined {
  try {
    const saved = localStorage.getItem(OWNERSHIP_FILTER_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed === 'all' || (Array.isArray(parsed) && parsed.length > 0)) {
        return parsed;
      }
    }
  } catch {
    // Ignore parse errors
  }
  return undefined; // Use API default
}

interface CreateTaskSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (dto: CreateTaskDto) => void;
  isCreating?: boolean;
  /** Pre-fill with client (for INDOOR tasks) */
  clientId?: number;
  clientName?: string;
  /** Preset task type — hides type selector */
  presetType?: TaskType;
}

export function CreateTaskSheet({
  open,
  onOpenChange,
  onCreate,
  isCreating,
  clientId,
  clientName,
  presetType,
}: CreateTaskSheetProps) {
  const [type, setType] = useState<TaskType>(presetType ?? (clientId ? 'INDOOR' : 'OUTDOOR'));
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [deadline, setDeadline] = useState('');
  const [selectedClientId, setSelectedClientId] = useState<number | undefined>(clientId);
  const [clientSearch, setClientSearch] = useState('');
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);

  // Get current ownership filter from localStorage (synced with Visits page)
  // Re-read when sheet opens to get the latest filter value
  const [ownershipFilter, setOwnershipFilter] = useState<OwnershipFilter | undefined>();
  useEffect(() => {
    if (open) {
      setOwnershipFilter(getOwnershipFilter());
    }
  }, [open]);

  // Fetch clients for selector (only when sheet is open and no preset client)
  // Uses the same filter as Visits page
  const { data: clients = [], isLoading: clientsLoading } = useQuery({
    queryKey: ['clients', 'for-selector', ownershipFilter],
    queryFn: () => clientsApi.getForSelector(ownershipFilter),
    enabled: open && !clientId,
    staleTime: 60_000,
  });

  // Filter clients by search
  const filteredClients = useMemo(() => {
    if (!clients) return [];
    if (!clientSearch.trim()) return clients;
    const search = clientSearch.toLowerCase();
    return clients.filter((c) =>
      c.name.toLowerCase().includes(search) ||
      c.address?.toLowerCase().includes(search)
    );
  }, [clients, clientSearch]);

  // Get selected client name
  const selectedClient = useMemo(() => {
    if (clientId && clientName) return { id: clientId, name: clientName };
    if (selectedClientId && clients) {
      return clients.find((c) => c.id === selectedClientId);
    }
    return undefined;
  }, [clientId, clientName, selectedClientId, clients]);

  // Reset form when sheet opens/closes or clientId changes
  useEffect(() => {
    if (open) {
      setType(presetType ?? (clientId ? 'INDOOR' : 'OUTDOOR'));
      setSelectedClientId(clientId);
      setClientSearch('');
    }
  }, [clientId, presetType, open]);

  const handleSubmit = () => {
    if (!title.trim()) return;
    // INDOOR/OUTDOOR requires client
    if (type !== 'PERSONAL' && !selectedClientId) return;

    onCreate({
      type,
      title: title.trim(),
      description: description.trim() || undefined,
      deadline: deadline || undefined,
      clientId: type !== 'PERSONAL' ? selectedClientId : undefined,
    });

    // Reset form
    setTitle('');
    setDescription('');
    setDeadline('');
    setSelectedClientId(undefined);
    setClientSearch('');
    onOpenChange(false);
  };

  const handleClose = () => {
    setTitle('');
    setDescription('');
    setDeadline('');
    setSelectedClientId(undefined);
    setClientSearch('');
    onOpenChange(false);
  };

  const handleSelectClient = (client: ClientForSelector) => {
    setSelectedClientId(client.id);
    setClientDropdownOpen(false);
    setClientSearch('');
  };

  // Determine if client selector should be shown
  const needsClientSelector = !clientId && type !== 'PERSONAL';

  // Show all 3 types when clientId provided, otherwise just OUTDOOR and PERSONAL
  const showIndoor = !!clientId;

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto" hideCloseButton>
        <SheetHeader>
          <div className="flex items-center justify-between">
            <div>
              <SheetTitle>Новая задача</SheetTitle>
              {clientName && (
                <SheetDescription className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  {clientName}
                </SheetDescription>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={(e) => {
                e.stopPropagation();
                handleClose();
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {/* Type selector */}
          {!presetType && <div className={cn('grid gap-2', showIndoor ? 'grid-cols-3' : 'grid-cols-2')}>
            {showIndoor && (
              <button
                type="button"
                onClick={() => setType('INDOOR')}
                className={cn(
                  'p-3 rounded-lg border-2 flex flex-col items-center gap-1 transition-all',
                  type === 'INDOOR'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-muted-foreground'
                )}
              >
                <ClipboardList className="h-5 w-5" />
                <span className="text-sm font-medium">У клиента</span>
                <span className="text-[10px] text-muted-foreground text-center">
                  Во время визита
                </span>
              </button>
            )}
            <button
              type="button"
              onClick={() => setType('OUTDOOR')}
              className={cn(
                'p-3 rounded-lg border-2 flex flex-col items-center gap-1 transition-all',
                type === 'OUTDOOR'
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-muted-foreground'
              )}
            >
              <Package className="h-5 w-5" />
              <span className="text-sm font-medium">Внешняя</span>
              <span className="text-[10px] text-muted-foreground text-center">
                До визита
              </span>
            </button>
            <button
              type="button"
              onClick={() => setType('PERSONAL')}
              className={cn(
                'p-3 rounded-lg border-2 flex flex-col items-center gap-1 transition-all',
                type === 'PERSONAL'
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-muted-foreground'
              )}
            >
              <User className="h-5 w-5" />
              <span className="text-sm font-medium">Личная</span>
              <span className="text-[10px] text-muted-foreground text-center">
                Без клиента
              </span>
            </button>
          </div>}

          {/* Client selector (for INDOOR/OUTDOOR without preset client) */}
          {needsClientSelector && (
            <div>
              <label className="text-sm font-medium">Клиент *</label>
              <DropdownMenu open={clientDropdownOpen} onOpenChange={setClientDropdownOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between font-normal mt-1"
                  >
                    {selectedClient ? (
                      <span className="truncate">{selectedClient.name}</span>
                    ) : (
                      <span className="text-muted-foreground">Выберите клиента</span>
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-(--radix-dropdown-menu-trigger-width) p-0 bg-background">
                  <div className="flex items-center border-b px-3 py-2">
                    <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                    <Input
                      placeholder="Поиск клиента..."
                      value={clientSearch}
                      onChange={(e) => setClientSearch(e.target.value)}
                      className="h-8 border-0 p-0 focus-visible:ring-0 focus-visible:ring-offset-0"
                    />
                  </div>
                  <div className="max-h-60 overflow-y-auto p-1">
                    {clientsLoading ? (
                      <div className="p-2 text-center text-sm text-muted-foreground">
                        Загрузка...
                      </div>
                    ) : filteredClients.length > 0 ? (
                      filteredClients.map((client) => (
                        <div
                          key={client.id}
                          className={cn(
                            'flex items-center gap-2 px-2 py-1.5 text-sm rounded-sm cursor-pointer',
                            'hover:bg-accent hover:text-accent-foreground',
                            selectedClientId === client.id && 'bg-accent'
                          )}
                          onClick={() => handleSelectClient(client)}
                        >
                          <Check
                            className={cn(
                              'h-4 w-4 flex-shrink-0',
                              selectedClientId === client.id ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="truncate">{client.name}</div>
                            {client.address && (
                              <div className="text-xs text-muted-foreground truncate">
                                {client.address}
                              </div>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="p-2 text-center text-sm text-muted-foreground">
                        {clientSearch ? 'Ничего не найдено' : 'Нет клиентов'}
                      </div>
                    )}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="text-sm font-medium">Название *</label>
            <Input
              className="mt-1"
              placeholder="Что нужно сделать?"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              autoFocus={!needsClientSelector || !!presetType}
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-sm font-medium">Описание</label>
            <Textarea
              className="mt-1"
              placeholder="Дополнительные детали..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>

          {/* Deadline */}
          <div>
            <label className="text-sm font-medium">Дедлайн</label>
            <Input
              type="date"
              className="mt-1"
              value={deadline}
              onChange={(e) => setDeadline(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
            />
          </div>

          {/* Submit */}
          <Button
            className="w-full"
            onClick={handleSubmit}
            disabled={!title.trim() || isCreating || (needsClientSelector && !selectedClientId)}
          >
            {isCreating ? 'Создание...' : 'Создать задачу'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
